/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : SaveAPMFileModal.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Account selection popup
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Feb 04, 2022
 *
 * @Version      : Version 1.0
 *
 * By            : <Author>
 * On            : <Date>
 * Description   : <Change Description>
 *
 *
 ******************************************************************************/

import React, { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Loader, Grid, Input, Icon } from "@scuf/common";
import { useNavigate } from "react-router-dom";
import { ErrorNotification } from "components/ErrorNotification";
import {
  setSaveFilePopUpModal,
  SaveFileName,
  selectGenerateFileNameLoading,
  selectGenerateFileNameSaveStatus,
  CheckExistingFileName,
  selectCheckExistFileNameLoading,
  selectExistingFileNameStatus,
} from "stores/ApmSettingsStore";
import { useForm } from "react-hook-form";
import { selectErrorNotification } from "stores/UIStore";
import { mandatoryNotes, fileGenerateSuccessMessage } from "./constants";
import "./SaveAPMFileModal.scss";

let startSearch: ReturnType<typeof setTimeout>;

interface ISaveFileName {
  fileName: string;
}

export const SaveAPMFileModal: FC = () => {
  const [modalOpen, setModalOpen] = useState(true);

  const dispatch = useDispatch();
  const { error } = useSelector(selectErrorNotification);

  const selectGenerateFileNameStatus = useSelector(
    selectGenerateFileNameSaveStatus
  );
  const selectGenerateFileNameLoader = useSelector(
    selectGenerateFileNameLoading
  );

  const selectCheckFileNameLoader = useSelector(
    selectCheckExistFileNameLoading
  );
  const selectIsFileNameExist = useSelector(selectExistingFileNameStatus);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISaveFileName>({
    mode: "all",
    defaultValues: {},
  });

  const onCheckFileNameHandler = (value: string) => {
    setValue("fileName", value, {
      shouldValidate: true,
    });
    clearTimeout(startSearch);
    startSearch = setTimeout(() => {
      value &&
        value.length >= 3 &&
        value.length <= 26 &&
        dispatch(CheckExistingFileName(value));
    }, 1000);
  };

  const onCloseHandler = () => {
    if (selectGenerateFileNameStatus) {
      navigate("/");
    }
    dispatch(setSaveFilePopUpModal(false));
    setModalOpen(false);
  };

  return (
    <Modal
      className="save-file-modal"
      size="small"
      closeIcon={true}
      closeOnDimmerClick={false}
      open={modalOpen}
      onClose={onCloseHandler}
    >
      <form>
        <Modal.Header>SAVE FILE</Modal.Header>
        <Modal.Content scrolling={false} className="modal-content">
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <ErrorNotification error={error} />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <div className="file-name-input">
                  {selectGenerateFileNameLoader ? (
                    <Loader minHeight={200}>
                      <div className="placeholder" />
                    </Loader>
                  ) : (
                    <Grid.Row>
                      {selectGenerateFileNameStatus ? (
                        <>
                          <div className="file-generate-succes-mark">
                            <Icon
                              root="building"
                              size="xlarge"
                              name="check"
                              color="green"
                            />
                          </div>
                          <div className="file-generate-succes-message">
                            {getValues()?.fileName} {fileGenerateSuccessMessage}
                          </div>
                        </>
                      ) : (
                        <>
                          <Grid.Column>
                            <Input
                              className="save-file-name"
                              name="fileName"
                              defaultValue={getValues()?.fileName}
                              register={register("fileName", {
                                required: {
                                  value: true,
                                  message: "Filename is required",
                                },
                                pattern: {
                                  value: /^[a-zA-Z0-9]+[a-zA-Z0-9\-_()]*$/,
                                  message:
                                    "File Name should contain letters (A-Z, a-z), digits (0-9), or the characters - _ ( )",
                                },
                                maxLength: {
                                  value: 26,
                                  message:
                                    "File Name should not contain more than 26 characters",
                                },
                                minLength: {
                                  value: 3,
                                  message:
                                    "File Name should be atleast 3 characters",
                                },
                              })}
                              placeholder="File Name"
                              onChange={(data) => {
                                onCheckFileNameHandler(data);
                              }}
                              label="File Name"
                              error={
                                (errors && errors?.fileName?.message) ||
                                (selectIsFileNameExist
                                  ? "File Name already exist"
                                  : "")
                              }
                              helperText={
                                getValues()?.fileName !== "" &&
                                getValues()?.fileName !== undefined &&
                                !selectIsFileNameExist &&
                                !selectCheckFileNameLoader &&
                                !(errors && errors?.fileName?.message)
                                  ? "File name is available"
                                  : ""
                              }
                            />
                          </Grid.Column>
                          <Grid.Column>
                            <span className="save-file-notes">
                              {mandatoryNotes}
                            </span>
                          </Grid.Column>
                        </>
                      )}
                    </Grid.Row>
                  )}
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Footer>
          <Grid>
            <Grid.Row>
              {selectGenerateFileNameStatus ? (
                <Grid.Column className="close-file-modal-footer-button">
                  <Button
                    type="secondary"
                    size="medium"
                    className="close-save-file-popup-button"
                    content="Close"
                    onClick={onCloseHandler}
                  />
                </Grid.Column>
              ) : (
                <Grid.Column className="save-file-modal-footer-button-container">
                  <Button
                    type="secondary"
                    disabled={selectCheckFileNameLoader}
                    size="medium"
                    className="cancel-save-file-popup-button"
                    content="Cancel"
                    onClick={onCloseHandler}
                  />
                  <Button
                    loading={selectCheckFileNameLoader ? true : false}
                    type="primary"
                    size="medium"
                    content="Generate APM File"
                    actionType="submit"
                    disabled={
                      selectCheckFileNameLoader ||
                      selectIsFileNameExist === true ||
                      selectIsFileNameExist === null
                    }
                    className="save-file-popup-button"
                    onClick={handleSubmit((data) =>
                      dispatch(SaveFileName(data?.fileName))
                    )}
                  />
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
